/**
 * Hand-rolled GoTrue REST auth client (RFC-0889 Sprint 4).
 *
 * Replaces supabase-js in the console. Speaks GoTrue's documented
 * `/auth/v1/*` REST surface over `fetch`, and ports the non-trivial supabase-js
 * behaviors that the app depends on:
 *   - session PERSISTENCE in the same localStorage key + JSON shape supabase-js
 *     used (`sb-<host-label>-auth-token`), via an injected quota-recovering
 *     storage — so existing logged-in users are NOT signed out on deploy, and
 *     the Playwright specs that seed that key keep working.
 *   - silent AUTO-REFRESH keyed off `expires_at` (fires ~60s before expiry).
 *   - `detectSessionInUrl` — parses the OAuth/recovery `#access_token=...` hash
 *     on load, fetches the user, and emits SIGNED_IN / PASSWORD_RECOVERY.
 *   - `onAuthStateChange` fan-out (INITIAL_SESSION / SIGNED_IN / SIGNED_OUT /
 *     TOKEN_REFRESHED / USER_UPDATED / PASSWORD_RECOVERY) + multi-tab logout
 *     via the window `storage` event.
 *
 * Return shapes match supabase-js (`{ data, error }`), and `AuthError` carries
 * `{ status, code, name, message }` so the existing `humanizeAuthError`
 * classification is unchanged.
 *
 * Discipline: NO `as` / `any` / `unknown` keywords (scripts/check-forbidden-ts-
 * syntax.mjs). The one place an inferred `any` leaks is `Response.json()`; it is
 * funnelled through `typeof`/`'key' in obj` runtime guards, exactly like the
 * existing `normalizeAuthError`.
 */
import { createNadDebug } from '../utils/debugLog'
import type {
  AuthChangeCallback,
  AuthChangeEvent,
  JsonValue,
  Session,
  Subscription,
  User,
  UserAppMetadata,
  UserIdentity,
  UserMetadata,
} from './gotrueTypes'

export type {
  AuthChangeCallback,
  AuthChangeEvent,
  JsonValue,
  Session,
  Subscription,
  User,
  UserAppMetadata,
  UserIdentity,
  UserMetadata,
} from './gotrueTypes'

const log = createNadDebug('dw:auth-client')
// feature=auth-cors — the outbound-auth-fetch boundary. The pre-fix trace went
// blind exactly here: whether the browser attaches credentials to the
// cross-origin auth request decides if the wildcard `ACAO: *` preflight passes.
const corsLog = createNadDebug('dw:auth-cors:request')

const TOKEN_TYPE_BEARER = 'bearer'
const APPLICATION_JSON = 'application/json'
const GRANT_TYPE = 'grant_type'
const REDIRECT_TO = 'redirect_to'
const AUTH_API_ERROR = 'AuthApiError'
const AUTH_RETRYABLE_ERROR = 'AuthRetryableFetchError'
const SESSION_MISSING_MESSAGE = 'Auth session missing'
const SESSION_PARSE_MESSAGE = 'Invalid session response from auth server'

// Refresh this many ms before `expires_at`. Comfortably larger than any test's
// clock skew, and far below the seeded 3600s/24h sessions so those never
// trigger an (unmocked) `/token` call on load.
const REFRESH_MARGIN_MS = 60_000

// On a transient (network) failure of the proactive timer refresh, re-arm the
// refresh timer after this short backoff so auto-refresh self-heals without
// waiting for the next getSession() call (RFC-0889 review).
const REFRESH_RETRY_MS = 5_000

// A `fetch`-shaped function the client actually uses (string URL + a small init
// subset). Narrower than the DOM `fetch` type so a test double is trivial to
// supply without `as`.
export interface FetchInit {
  method: string
  headers: { [key: string]: string }
  body?: string
}
export type FetchLike = (input: string, init: FetchInit) => Promise<Response>

export interface GoTrueStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

export interface GoTrueClientOptions {
  /** Auth base URL with no trailing slash; endpoints are appended (`/token`, `/user`, …). */
  authBaseUrl: string
  anonKey: string
  storage: GoTrueStorage
  storageKey: string
  autoRefreshToken?: boolean
  persistSession?: boolean
  detectSessionInUrl?: boolean
  /** Injectable for tests. Defaults to a wrapper over global `fetch`. */
  fetchImpl?: FetchLike
  /** Injectable for tests. Defaults to `window` (or null when unavailable). */
  windowRef?: Window | null
  /** Injectable clock in ms epoch. Defaults to `Date.now`. */
  now?: () => number
}

// ---- result / argument shapes (match supabase-js destructuring) -------------

export interface AuthResult {
  data: { user: User | null; session: Session | null }
  error: AuthError | null
}
export interface SessionResult {
  data: { session: Session | null }
  error: AuthError | null
}
export interface RefreshResult {
  data: { session: Session | null; user: User | null }
  error: AuthError | null
}
export interface UpdateUserResult {
  data: { user: User | null }
  error: AuthError | null
}
export interface OAuthResult {
  data: { provider: string; url: string | null }
  error: AuthError | null
}
export interface RecoverResult {
  data: Record<string, never>
  error: AuthError | null
}
export interface SignOutResult {
  error: AuthError | null
}
export interface SubscriptionResult {
  data: { subscription: Subscription }
}

export interface SignInWithPasswordCredentials {
  email?: string
  phone?: string
  password: string
  options?: { captchaToken?: string }
}
export interface SignUpCredentials {
  email?: string
  phone?: string
  password: string
  options?: { emailRedirectTo?: string; data?: { [key: string]: JsonValue }; captchaToken?: string }
}
export interface SignInWithOtpCredentials {
  email?: string
  phone?: string
  options?: {
    emailRedirectTo?: string
    shouldCreateUser?: boolean
    data?: { [key: string]: JsonValue }
    captchaToken?: string
  }
}
export interface VerifyOtpParams {
  type: string
  token_hash?: string
  token?: string
  email?: string
  phone?: string
}
export interface SignInWithOAuthParams {
  provider: string
  options?: {
    redirectTo?: string
    scopes?: string
    skipBrowserRedirect?: boolean
    queryParams?: { [key: string]: string }
  }
}
export interface UpdateUserAttributes {
  email?: string
  phone?: string
  password?: string
  nonce?: string
  data?: { [key: string]: JsonValue }
}
export interface ResetPasswordOptions {
  redirectTo?: string
  captchaToken?: string
}

/**
 * supabase-js-parity auth error. Extends `Error` so callers' `err instanceof
 * Error` guards keep working, and carries `status`/`code` so `humanizeAuthError`
 * classification is unchanged.
 */
export class AuthError extends Error {
  status?: number
  code?: string
  constructor(message: string, status?: number, code?: string, name: string = AUTH_API_ERROR) {
    super(message)
    this.name = name
    this.status = status
    this.code = code
  }
}

export class GoTrueClient {
  private readonly authBaseUrl: string
  private readonly anonKey: string
  private readonly storage: GoTrueStorage
  private readonly storageKey: string
  private readonly autoRefreshToken: boolean
  private readonly persistSession: boolean
  private readonly detectSessionInUrl: boolean
  private readonly fetchImpl: FetchLike
  private readonly windowRef: Window | null
  private readonly now: () => number

  private currentSession: Session | null = null
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  // In-flight /token refresh shared by all concurrent callers, so a one-time-use
  // refresh token is spent exactly once per rotation (RFC-0889 review).
  private refreshPromise: Promise<RefreshResult> | null = null
  private readonly subscribers = new Map<string, AuthChangeCallback>()
  private subCounter = 0
  private readonly initializePromise: Promise<void>

  constructor(options: GoTrueClientOptions) {
    this.authBaseUrl = options.authBaseUrl.replace(/\/+$/, '')
    this.anonKey = options.anonKey
    this.storage = options.storage
    this.storageKey = options.storageKey
    this.autoRefreshToken = options.autoRefreshToken ?? true
    this.persistSession = options.persistSession ?? true
    this.detectSessionInUrl = options.detectSessionInUrl ?? true
    // Default auth fetch is NONCREDENTIALED. auth.* is a DIFFERENT origin from
    // api.* — the DREAMWRIGHT_PROJECT affinity cookie (CLAUDE.md "every console
    // fetch must send credentials:'include'") belongs to api.*, NOT auth.*. The
    // authoritative Console fetch policy documents this seam in
    // `src/api/client.ts` (the "AUTH EXCEPTION (RFC-1015)" note). GoTrue
    // is stateless (bearer tokens + localStorage, no Set-Cookie) and the auth LB
    // returns wildcard `Access-Control-Allow-Origin: *` (infra/terraform/auth_lb.tf).
    // A credentialed request makes the browser hard-block that wildcard preflight,
    // so signup/login never fire. Explicit 'omit' (not the implicit same-origin
    // default) keeps the noncredentialed-auth invariant deployment-independent even
    // when VITE_GOTRUE_URL points at a same-origin proxy.
    this.fetchImpl =
      options.fetchImpl ??
      ((input, init) => {
        corsLog({ event: 'auth-fetch', url: input, method: init.method, credentials: 'omit' })
        return fetch(input, { ...init, credentials: 'omit' })
      })
    this.windowRef = options.windowRef ?? (typeof window !== 'undefined' ? window : null)
    this.now = options.now ?? (() => Date.now())

    if (this.windowRef !== null && this.persistSession) {
      this.windowRef.addEventListener('storage', this.handleStorageEvent)
    }
    // Kick off recovery/URL-detection immediately; every public method awaits
    // this so callers observe a fully-initialized client (getSession must see
    // the fragment-built session before ResetPasswordPage compares tokens).
    this.initializePromise = this.initialize()
  }

  // ---- lifecycle ------------------------------------------------------------

  private async initialize(): Promise<void> {
    try {
      const fromUrl = await this.initializeFromUrl()
      if (fromUrl) return
      const recovered = this.recoverFromStorage()
      if (recovered === null) return
      this.currentSession = recovered
      log({ event: 'session-load', hasSession: true, expiresIn: recovered.expires_in })
      if (
        this.autoRefreshToken &&
        this.isExpiredWithinMargin(recovered) &&
        recovered.refresh_token.length > 0
      ) {
        await this.doRefresh(recovered)
      } else {
        this.scheduleRefresh(recovered)
      }
    } catch {
      // Initialization must never throw — a corrupt stored session simply
      // leaves the client signed-out.
    }
  }

  dispose(): void {
    this.clearRefreshTimer()
    this.subscribers.clear()
    if (this.windowRef !== null && this.persistSession) {
      this.windowRef.removeEventListener('storage', this.handleStorageEvent)
    }
  }

  // ---- public API (supabase-js `auth.*` parity) -----------------------------

  async signInWithPassword(credentials: SignInWithPasswordCredentials): Promise<AuthResult> {
    await this.initializePromise
    const body: { [key: string]: JsonValue } = { password: credentials.password }
    if (typeof credentials.email === 'string') body.email = credentials.email
    if (typeof credentials.phone === 'string') body.phone = credentials.phone
    log({ event: 'signin-password:start', hasEmail: typeof credentials.email === 'string' })
    this.applyCaptcha(body, credentials.options?.captchaToken)
    const result = await this.request('/token', { method: 'POST', body, query: { [GRANT_TYPE]: 'password' } })
    if (!result.ok) {
      const error = result.networkError ?? this.errorFromResponse(result.status, result.data)
      log({ event: 'signin-password:error', status: result.status, code: error.code ?? null })
      return { data: { user: null, session: null }, error }
    }
    const session = parseSession(result.data)
    if (session === null) {
      return { data: { user: null, session: null }, error: new AuthError(SESSION_PARSE_MESSAGE, result.status) }
    }
    this.setSession(session, 'SIGNED_IN')
    log({ event: 'signin-password:ok', userId: session.user.id })
    return { data: { user: session.user, session }, error: null }
  }

  async signUp(credentials: SignUpCredentials): Promise<AuthResult> {
    await this.initializePromise
    const body: { [key: string]: JsonValue } = { password: credentials.password }
    if (typeof credentials.email === 'string') body.email = credentials.email
    if (typeof credentials.phone === 'string') body.phone = credentials.phone
    if (credentials.options?.data !== undefined) body.data = credentials.options.data
    const query: QueryParams = {}
    if (typeof credentials.options?.emailRedirectTo === 'string') {
      query[REDIRECT_TO] = credentials.options.emailRedirectTo
    }
    this.applyCaptcha(body, credentials.options?.captchaToken)
    const result = await this.request('/signup', { method: 'POST', body, query })
    if (!result.ok) {
      const error = result.networkError ?? this.errorFromResponse(result.status, result.data)
      return { data: { user: null, session: null }, error }
    }
    // Auto-confirm returns a full session; email-confirmation-required returns a
    // bare user (with `identities: []` for the enumeration-safe already-exists case).
    const session = parseSession(result.data)
    if (session !== null) {
      this.setSession(session, 'SIGNED_IN')
      return { data: { user: session.user, session }, error: null }
    }
    return { data: { user: parseUser(result.data), session: null }, error: null }
  }

  async signInWithOtp(credentials: SignInWithOtpCredentials): Promise<AuthResult> {
    await this.initializePromise
    const body: { [key: string]: JsonValue } = {}
    if (typeof credentials.email === 'string') body.email = credentials.email
    if (typeof credentials.phone === 'string') body.phone = credentials.phone
    body.create_user = credentials.options?.shouldCreateUser ?? true
    if (credentials.options?.data !== undefined) body.data = credentials.options.data
    const query: QueryParams = {}
    if (typeof credentials.options?.emailRedirectTo === 'string') {
      query[REDIRECT_TO] = credentials.options.emailRedirectTo
    }
    this.applyCaptcha(body, credentials.options?.captchaToken)
    const result = await this.request('/otp', { method: 'POST', body, query })
    if (!result.ok) {
      const error = result.networkError ?? this.errorFromResponse(result.status, result.data)
      return { data: { user: null, session: null }, error }
    }
    return { data: { user: null, session: null }, error: null }
  }

  async verifyOtp(params: VerifyOtpParams): Promise<AuthResult> {
    await this.initializePromise
    const body: { [key: string]: JsonValue } = { type: params.type }
    if (typeof params.token_hash === 'string') body.token_hash = params.token_hash
    if (typeof params.token === 'string') body.token = params.token
    if (typeof params.email === 'string') body.email = params.email
    if (typeof params.phone === 'string') body.phone = params.phone
    const result = await this.request('/verify', { method: 'POST', body })
    if (!result.ok) {
      const error = result.networkError ?? this.errorFromResponse(result.status, result.data)
      return { data: { user: null, session: null }, error }
    }
    // A successful recovery verify returns a session that authorizes the
    // immediately-following updateUser({ password }); persist it. A recovery
    // verify emits PASSWORD_RECOVERY (supabase-js parity + the signal
    // ResetPasswordPage authorizes on), not SIGNED_IN.
    const session = parseSession(result.data)
    if (session !== null) {
      this.setSession(session, params.type === 'recovery' ? 'PASSWORD_RECOVERY' : 'SIGNED_IN')
      return { data: { user: session.user, session }, error: null }
    }
    return { data: { user: parseUser(result.data), session: null }, error: null }
  }

  async signInWithOAuth(params: SignInWithOAuthParams): Promise<OAuthResult> {
    await this.initializePromise
    const query = new URLSearchParams()
    query.set('provider', params.provider)
    if (typeof params.options?.redirectTo === 'string') query.set(REDIRECT_TO, params.options.redirectTo)
    if (typeof params.options?.scopes === 'string') query.set('scopes', params.options.scopes)
    if (params.options?.queryParams !== undefined) {
      for (const key of Object.keys(params.options.queryParams)) {
        query.set(key, params.options.queryParams[key])
      }
    }
    const url = `${this.authBaseUrl}/authorize?${query.toString()}`
    log({ event: 'oauth:redirect', provider: params.provider })
    if (params.options?.skipBrowserRedirect !== true && this.windowRef !== null) {
      this.windowRef.location.assign(url)
    }
    return { data: { provider: params.provider, url }, error: null }
  }

  async signOut(options?: { scope?: string }): Promise<SignOutResult> {
    await this.initializePromise
    const scope = options?.scope ?? 'global'
    const token = this.currentSession?.access_token ?? null
    const hadSession = this.currentSession !== null
    // scope 'others' revokes OTHER sessions only — the current session stays
    // valid, so DO NOT clear local state (supabase-js parity). For 'global' /
    // 'local', clear FIRST and unconditionally: a hung or 401 logout must never
    // strand the client signed-in (RFC-0851 invalid-session loop-break).
    if (scope !== 'others') {
      this.removeSession()
      this.emit('SIGNED_OUT', null)
      log({ event: 'signout:clear', hadSession })
    }
    if (token !== null) {
      // Best-effort revoke; ignore network/HTTP errors on an already-invalid session.
      await this.request('/logout', { method: 'POST', jwt: token, query: { scope } })
    }
    return { error: null }
  }

  async getSession(): Promise<SessionResult> {
    await this.initializePromise
    const session = this.currentSession
    if (
      session !== null &&
      this.autoRefreshToken &&
      this.isExpiredWithinMargin(session) &&
      session.refresh_token.length > 0
    ) {
      const refreshed = await this.doRefresh(session)
      // A proactive (near-expiry) refresh that fails transiently leaves the
      // current session intact — the existing token may still be valid for up to
      // REFRESH_MARGIN_MS, so return it rather than stranding the caller with
      // null over a network blip. A hard rejection already cleared the session.
      if (refreshed.error !== null && this.currentSession !== null) {
        return { data: { session: this.currentSession }, error: null }
      }
      return { data: { session: refreshed.data.session }, error: refreshed.error }
    }
    return { data: { session }, error: null }
  }

  async refreshSession(currentSession?: Session | null): Promise<RefreshResult> {
    await this.initializePromise
    return this.doRefresh(currentSession)
  }

  async updateUser(attributes: UpdateUserAttributes): Promise<UpdateUserResult> {
    await this.initializePromise
    const token = this.currentSession?.access_token ?? null
    if (token === null) {
      return { data: { user: null }, error: new AuthError(SESSION_MISSING_MESSAGE, 401) }
    }
    const body: { [key: string]: JsonValue } = {}
    if (typeof attributes.email === 'string') body.email = attributes.email
    if (typeof attributes.phone === 'string') body.phone = attributes.phone
    if (typeof attributes.password === 'string') body.password = attributes.password
    if (typeof attributes.nonce === 'string') body.nonce = attributes.nonce
    if (attributes.data !== undefined) body.data = attributes.data
    const result = await this.request('/user', { method: 'PUT', jwt: token, body })
    if (!result.ok) {
      const error = result.networkError ?? this.errorFromResponse(result.status, result.data)
      log({ event: 'update-user:error', status: result.status, code: error.code ?? null })
      return { data: { user: null }, error }
    }
    const user = parseUser(result.data)
    // Merge the updated user into the current session + re-persist so a reload
    // re-hydrates the new user (matches supabase-js updateUser).
    const current = this.currentSession
    if (current !== null) {
      this.applySession({ ...current, user })
    }
    this.emit('USER_UPDATED', this.currentSession)
    log({ event: 'update-user:ok', userId: user.id })
    return { data: { user }, error: null }
  }

  async resetPasswordForEmail(email: string, options?: ResetPasswordOptions): Promise<RecoverResult> {
    await this.initializePromise
    const query: QueryParams = {}
    if (typeof options?.redirectTo === 'string') query[REDIRECT_TO] = options.redirectTo
    const body: { [key: string]: JsonValue } = { email }
    this.applyCaptcha(body, options?.captchaToken)
    const result = await this.request('/recover', { method: 'POST', body, query })
    if (!result.ok) {
      const error = result.networkError ?? this.errorFromResponse(result.status, result.data)
      return { data: {}, error }
    }
    return { data: {}, error: null }
  }

  onAuthStateChange(callback: AuthChangeCallback): SubscriptionResult {
    this.subCounter += 1
    const id = `sub-${this.subCounter}`
    this.subscribers.set(id, callback)
    const subscription: Subscription = { id, unsubscribe: () => this.subscribers.delete(id) }
    // supabase-js parity: deliver INITIAL_SESSION to this subscriber once init settles.
    void this.initializePromise.then(() => {
      if (!this.subscribers.has(id)) return
      try {
        callback('INITIAL_SESSION', this.currentSession)
      } catch {
        // A subscriber throwing must not break init.
      }
    })
    return { data: { subscription } }
  }

  // ---- internals ------------------------------------------------------------

  private async initializeFromUrl(): Promise<boolean> {
    const win = this.windowRef
    if (!this.detectSessionInUrl || win === null) return false
    const rawHash = win.location.hash
    if (typeof rawHash !== 'string' || rawHash.length < 2) return false
    const params = new URLSearchParams(rawHash.startsWith('#') ? rawHash.slice(1) : rawHash)
    // GoTrue can redirect back with an error fragment instead of tokens (e.g. the
    // user denied OAuth consent: #error=access_denied&error_description=...).
    // supabase-js surfaced this rather than silently leaving the user signed-out
    // with a lingering hash. Log it and strip the hash so a reload doesn't
    // re-trigger it and it doesn't linger in history/referrers.
    const errorCode = params.get('error')
    const errorDescription = params.get('error_description')
    if (errorCode !== null || errorDescription !== null) {
      log({ event: 'url-session:error', errorCode: errorCode ?? null, errorDescription: errorDescription ?? null })
      try {
        win.history.replaceState(null, '', win.location.pathname + win.location.search)
      } catch {
        // history may be unavailable in some embeddings.
      }
      return false
    }
    const accessToken = params.get('access_token')
    const refreshToken = params.get('refresh_token')
    if (accessToken === null || refreshToken === null) return false

    const user = await this.fetchUser(accessToken)
    if (user === null) return false

    const expiresInRaw = params.get('expires_in')
    const expiresIn = expiresInRaw !== null && Number.isFinite(Number(expiresInRaw)) ? Number(expiresInRaw) : 0
    const session: Session = {
      access_token: accessToken,
      refresh_token: refreshToken,
      expires_in: expiresIn,
      token_type: params.get('token_type') ?? TOKEN_TYPE_BEARER,
      user,
    }
    // Prefer the hash's own expires_at (supabase-js parity); applySession only
    // falls back to now + expires_in when it is absent/non-numeric. Recomputing
    // it from now would over-extend a stale or clock-skewed callback fragment.
    const expiresAtRaw = params.get('expires_at')
    if (expiresAtRaw !== null && Number.isFinite(Number(expiresAtRaw))) {
      session.expires_at = Number(expiresAtRaw)
    }
    const providerToken = params.get('provider_token')
    if (providerToken !== null) session.provider_token = providerToken
    const providerRefreshToken = params.get('provider_refresh_token')
    if (providerRefreshToken !== null) session.provider_refresh_token = providerRefreshToken
    const type = params.get('type')

    this.applySession(session)
    log({ event: 'url-session:applied', type: type ?? null })
    // Strip the token fragment so it does not linger in history/referrers.
    try {
      win.history.replaceState(null, '', win.location.pathname + win.location.search)
    } catch {
      // history may be unavailable in some embeddings.
    }
    this.emit('SIGNED_IN', session)
    if (type === 'recovery') this.emit('PASSWORD_RECOVERY', session)
    return true
  }

  private async fetchUser(accessToken: string): Promise<User | null> {
    const result = await this.request('/user', { method: 'GET', jwt: accessToken })
    if (!result.ok) return null
    const user = parseUser(result.data)
    // Fail loud on a missing id (parity with parseSession): an empty-id user must
    // not be built into a persisted OAuth-callback session.
    return user.id.length > 0 ? user : null
  }

  private async doRefresh(currentSession?: Session | null): Promise<RefreshResult> {
    const base = currentSession ?? this.currentSession
    const refreshToken = base?.refresh_token ?? null
    if (refreshToken === null || refreshToken.length === 0) {
      return { data: { session: null, user: null }, error: new AuthError(SESSION_MISSING_MESSAGE, 400) }
    }
    // In-flight dedup: getSession(), the auto-refresh timer, and
    // AuthContext.getAccessToken can all race here. GoTrue rotates the refresh
    // token one-time-use, so a second concurrent POST /token with the same token
    // would 401 and spuriously sign a valid user out. Share ONE request.
    const existing = this.refreshPromise
    if (existing !== null) return existing
    const pending = this.performRefresh(refreshToken)
    this.refreshPromise = pending
    try {
      return await pending
    } finally {
      this.refreshPromise = null
    }
  }

  private async performRefresh(refreshToken: string): Promise<RefreshResult> {
    log({ event: 'refresh:fired' })
    const result = await this.request('/token', {
      method: 'POST',
      body: { refresh_token: refreshToken },
      query: { [GRANT_TYPE]: 'refresh_token' },
    })
    if (!result.ok) {
      const error = result.networkError ?? this.errorFromResponse(result.status, result.data)
      log({ event: 'refresh:failed', status: result.status, code: error.code ?? null })
      // A transient network error leaves the session intact so a blip does not
      // log the user out.
      if (result.networkError === null) {
        // Definitive HTTP rejection: THIS refresh token is dead. But another tab
        // may have already rotated the session and persisted a fresh one before
        // our storage listener caught up — adopt it instead of signing out.
        const adopted = this.adoptRotatedSession(refreshToken)
        if (adopted !== null) {
          this.currentSession = adopted
          this.scheduleRefresh(adopted)
          this.emit('TOKEN_REFRESHED', adopted)
          log({ event: 'refresh:adopted-stored', userId: adopted.user.id })
          return { data: { session: adopted, user: adopted.user }, error: null }
        }
        this.removeSession()
        this.emit('SIGNED_OUT', null)
      }
      return { data: { session: null, user: null }, error }
    }
    const session = parseSession(result.data)
    if (session === null) {
      return { data: { session: null, user: null }, error: new AuthError(SESSION_PARSE_MESSAGE, result.status) }
    }
    this.setSession(session, 'TOKEN_REFRESHED')
    log({ event: 'refresh:ok', userId: session.user.id })
    return { data: { session, user: session.user }, error: null }
  }

  /**
   * On a hard refresh rejection, re-read persisted storage: if another tab has
   * already rotated the session (a stored session whose refresh_token differs
   * from the dead one we just tried), return it so the caller adopts it rather
   * than signing a still-valid user out. Returns null when no newer session
   * exists (genuinely dead → sign out).
   */
  private adoptRotatedSession(deadRefreshToken: string): Session | null {
    const stored = this.recoverFromStorage()
    if (stored === null || stored.refresh_token.length === 0) return null
    if (stored.refresh_token === deadRefreshToken) return null
    return stored
  }

  private recoverFromStorage(): Session | null {
    if (!this.persistSession) return null
    let raw: string | null = null
    try {
      raw = this.storage.getItem(this.storageKey)
    } catch {
      return null
    }
    if (raw === null) return null
    let parsed: JsonValue
    try {
      parsed = JSON.parse(raw)
    } catch {
      return null
    }
    return parseSession(parsed)
  }

  private applySession(session: Session): void {
    if (session.expires_at === undefined) {
      // Derive an absolute expiry so auto-refresh has something to key off. With
      // a positive expires_in, project it forward; with none (expires_in <= 0,
      // e.g. a degenerate token/callback), treat the session as already-expired
      // so a refresh is attempted immediately instead of the token going stale
      // forever (RFC-0889 review).
      const nowSec = Math.floor(this.now() / 1000)
      session.expires_at = session.expires_in > 0 ? nowSec + session.expires_in : nowSec
    }
    this.currentSession = session
    this.persist(session)
    this.scheduleRefresh(session)
  }

  private setSession(session: Session, event: AuthChangeEvent): void {
    this.applySession(session)
    this.emit(event, session)
  }

  private persist(session: Session): void {
    if (!this.persistSession) return
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(session))
      log({ event: 'session-persist', userId: session.user.id })
    } catch {
      // The injected quota-recovering storage already falls back to memory.
    }
  }

  private removeSession(): void {
    this.currentSession = null
    this.clearRefreshTimer()
    if (!this.persistSession) return
    try {
      this.storage.removeItem(this.storageKey)
    } catch {
      // Ignore storage errors during sign-out.
    }
  }

  private scheduleRefresh(session: Session): void {
    this.clearRefreshTimer()
    if (!this.autoRefreshToken) return
    if (session.expires_at === undefined || session.refresh_token.length === 0) return
    const delay = Math.max(0, session.expires_at * 1000 - this.now() - REFRESH_MARGIN_MS)
    log({ event: 'refresh:scheduled', delayMs: delay })
    this.armRefreshTimer(delay)
  }

  private armRefreshTimer(delay: number): void {
    this.clearRefreshTimer()
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null
      void this.runTimerRefresh()
    }, delay)
  }

  private async runTimerRefresh(): Promise<void> {
    const result = await this.doRefresh()
    // A transient network failure leaves the session intact but consumes this
    // one-shot timer; re-arm with a short backoff so proactive refresh recovers
    // without waiting for the next getSession(). A success already scheduled the
    // next refresh (refreshTimer set) and a hard rejection cleared the session.
    if (
      result.error !== null &&
      result.error.name === AUTH_RETRYABLE_ERROR &&
      this.autoRefreshToken &&
      this.refreshTimer === null &&
      (this.currentSession?.refresh_token.length ?? 0) > 0
    ) {
      log({ event: 'refresh:retry-scheduled', delayMs: REFRESH_RETRY_MS })
      this.armRefreshTimer(REFRESH_RETRY_MS)
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  // True when the token is expired OR within REFRESH_MARGIN_MS of expiry — the
  // synchronous "should I refresh before handing this token out?" check used by
  // getSession() + init, so a token seconds from expiry is refreshed proactively
  // instead of being used for an API call that then 401s (RFC-0889 review).
  private isExpiredWithinMargin(session: Session): boolean {
    if (session.expires_at === undefined) return false
    return session.expires_at * 1000 - this.now() <= REFRESH_MARGIN_MS
  }

  private emit(event: AuthChangeEvent, session: Session | null): void {
    log({ event: 'auth-event', authEvent: event, hasSession: session !== null })
    for (const callback of this.subscribers.values()) {
      try {
        callback(event, session)
      } catch {
        // One subscriber throwing must not stop the fan-out.
      }
    }
  }

  private readonly handleStorageEvent = (e: StorageEvent): void => {
    // `key === null` is storage.clear(); otherwise only react to our key.
    if (e.key !== this.storageKey && e.key !== null) return
    const raw = e.newValue
    if (raw === null) {
      if (this.currentSession !== null) {
        this.currentSession = null
        this.clearRefreshTimer()
        this.emit('SIGNED_OUT', null)
      }
      return
    }
    let parsed: JsonValue
    try {
      parsed = JSON.parse(raw)
    } catch {
      return
    }
    const session = parseSession(parsed)
    if (session === null) return
    const wasSignedIn = this.currentSession !== null
    if (this.currentSession?.access_token === session.access_token) {
      // Same token but the stored value changed in another tab (e.g. updateUser
      // → new user_metadata). supabase-js broadcasts USER_UPDATED so profile,
      // name, and locale stay in sync across tabs.
      this.currentSession = session
      this.emit('USER_UPDATED', session)
      return
    }
    // Do NOT re-persist: the other tab already wrote this value.
    this.currentSession = session
    this.scheduleRefresh(session)
    this.emit(wasSignedIn ? 'TOKEN_REFRESHED' : 'SIGNED_IN', session)
  }

  // supabase-js sends the hCaptcha/Turnstile token as
  // `gotrue_meta_security: { captcha_token }` on the auth flows that accept it.
  // Only added when a caller supplies a token, so current calls are unchanged.
  private applyCaptcha(body: { [key: string]: JsonValue }, token: string | undefined): void {
    if (typeof token === 'string' && token.length > 0) {
      body.gotrue_meta_security = { captcha_token: token }
    }
  }

  private async request(
    path: string,
    init: { method: string; jwt?: string; body?: { [key: string]: JsonValue }; query?: QueryParams },
  ) {
    const headers: { [key: string]: string } = {
      apikey: this.anonKey,
      Authorization: `Bearer ${init.jwt ?? this.anonKey}`,
    }
    const fetchInit: FetchInit = { method: init.method, headers }
    if (init.body !== undefined) {
      headers['Content-Type'] = APPLICATION_JSON
      fetchInit.body = JSON.stringify(init.body)
    }
    const url = this.buildUrl(path, init.query)
    corsLog({ event: 'auth-request', url, method: init.method })
    try {
      const response = await this.fetchImpl(url, fetchInit)
      const data = await readJson(response)
      return { ok: response.ok, status: response.status, data, networkError: null }
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught)
      return {
        ok: false,
        status: 0,
        data: {},
        networkError: new AuthError(message, 0, undefined, AUTH_RETRYABLE_ERROR),
      }
    }
  }

  private buildUrl(path: string, query?: QueryParams): string {
    const base = `${this.authBaseUrl}${path}`
    if (query === undefined) return base
    const params = new URLSearchParams()
    for (const key of Object.keys(query)) {
      const value = query[key]
      if (typeof value === 'string' && value.length > 0) params.set(key, value)
    }
    const qs = params.toString()
    return qs.length > 0 ? `${base}?${qs}` : base
  }

  private errorFromResponse(status: number, data: { [key: string]: JsonValue }): AuthError {
    const message = getErrorMessage(data)
    // supabase-js parity: the string `error_code` is the classification key.
    // Never surface GoTrue's numeric `code` (which is the HTTP status).
    const code =
      typeof data.error_code === 'string'
        ? data.error_code
        : typeof data.code === 'string'
          ? data.code
          : undefined
    return new AuthError(message, status, code)
  }
}

type QueryParams = { [key: string]: string | undefined }

// ---- module-level pure helpers (guard-based, no `as`/`any`/`unknown`) --------

async function readJson(response: Response) {
  try {
    return await response.json()
  } catch {
    return {}
  }
}

function getErrorMessage(data: { [key: string]: JsonValue }): string {
  if (typeof data.msg === 'string') return data.msg
  if (typeof data.message === 'string') return data.message
  if (typeof data.error_description === 'string') return data.error_description
  if (typeof data.error === 'string') return data.error
  try {
    return JSON.stringify(data)
  } catch {
    return String(data)
  }
}

function parseSession(raw: JsonValue): Session | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null
  const accessToken = raw.access_token
  const refreshToken = raw.refresh_token
  if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') return null
  const rawUser = raw.user
  if (rawUser === null || typeof rawUser !== 'object' || Array.isArray(rawUser)) return null

  const user = parseUser(rawUser)
  // Fail loud on a missing/empty user id: an empty id breaks hasPasswordIdentity
  // + RLS and the backend AuthUser.from_json raises on it. Treat the session as
  // unparseable so callers sign out instead of persisting a broken identity.
  if (user.id.length === 0) return null
  const session: Session = {
    access_token: accessToken,
    refresh_token: refreshToken,
    expires_in: typeof raw.expires_in === 'number' ? raw.expires_in : 0,
    token_type: typeof raw.token_type === 'string' ? raw.token_type : TOKEN_TYPE_BEARER,
    user,
  }
  if (typeof raw.expires_at === 'number') session.expires_at = raw.expires_at
  if (typeof raw.provider_token === 'string') session.provider_token = raw.provider_token
  if (typeof raw.provider_refresh_token === 'string') {
    session.provider_refresh_token = raw.provider_refresh_token
  }
  return session
}

function parseUser(raw: JsonValue): User {
  const source: { [key: string]: JsonValue } =
    raw !== null && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}

  const appMetadata: UserAppMetadata = {}
  fillRecord(appMetadata, source.app_metadata)
  const userMetadata: UserMetadata = {}
  fillRecord(userMetadata, source.user_metadata)

  const user: User = {
    id: typeof source.id === 'string' ? source.id : '',
    app_metadata: appMetadata,
    user_metadata: userMetadata,
  }
  if (typeof source.aud === 'string') user.aud = source.aud
  if (typeof source.role === 'string') user.role = source.role
  if (typeof source.email === 'string') user.email = source.email
  if (typeof source.phone === 'string') user.phone = source.phone
  if (typeof source.created_at === 'string') user.created_at = source.created_at
  if (typeof source.updated_at === 'string') user.updated_at = source.updated_at
  const identities = parseIdentities(source.identities)
  if (identities !== undefined) user.identities = identities
  return user
}

function parseIdentities(raw: JsonValue): UserIdentity[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const identities: UserIdentity[] = []
  for (const entry of raw) {
    const provider =
      entry !== null &&
      typeof entry === 'object' &&
      !Array.isArray(entry) &&
      typeof entry.provider === 'string'
        ? entry.provider
        : ''
    const identity: UserIdentity = { provider }
    fillRecord(identity, entry)
    identities.push(identity)
  }
  return identities
}

function fillRecord(target: { [key: string]: JsonValue | undefined }, source: JsonValue): void {
  if (source === null || typeof source !== 'object' || Array.isArray(source)) return
  for (const key of Object.keys(source)) {
    const value = toJsonValue(source[key])
    if (value !== undefined) target[key] = value
  }
}

function toJsonValue(value: JsonValue): JsonValue | undefined {
  if (value === null) return null
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value
  }
  if (Array.isArray(value)) {
    const out: JsonValue[] = []
    for (const item of value) {
      const coerced = toJsonValue(item)
      out.push(coerced === undefined ? null : coerced)
    }
    return out
  }
  if (typeof value === 'object') {
    const out: { [key: string]: JsonValue } = {}
    for (const key of Object.keys(value)) {
      const coerced = toJsonValue(value[key])
      if (coerced !== undefined) out[key] = coerced
    }
    return out
  }
  return undefined
}
